import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { PasswordInput } from '@/shared/components/ui/password-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/cn';
import {
  createUsuario,
  updateUsuario,
  type UsuarioItem,
} from '@/shared/api/controllers/usuarios';
import {
  PAIS_OPTIONS,
  PERFILES,
  isPerfilValidForCountries,
  type PaisLabel,
  type Perfil,
} from '../../constants';
import {
  createUsuarioSchema,
  editUsuarioSchema,
  emptyCreateUsuario,
  type EditUsuarioValues,
} from './schema';
import { createFormToFields, editFormToFields, itemToEditForm } from './mappers';
import { USUARIOS_QUERY_KEY } from './queries';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  usuario?: UsuarioItem | null;
  existingUsuarios: UsuarioItem[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export function UsuarioDialog({
  open,
  onOpenChange,
  usuario,
  existingUsuarios,
  onSuccess,
  onError,
}: Props) {
  const queryClient = useQueryClient();
  const mode: 'create' | 'edit' = usuario ? 'edit' : 'create';

  const defaults = useMemo<EditUsuarioValues>(
    () => (usuario ? itemToEditForm(usuario) : emptyCreateUsuario),
    [usuario],
  );

  const form = useForm<EditUsuarioValues>({
    resolver: zodResolver(mode === 'edit' ? editUsuarioSchema : createUsuarioSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (open) {
      form.reset(defaults);
      setRoleClearedReason(null);
    }
  }, [open, defaults, form]);

  const perfil = form.watch('perfil');
  const watchedPaises = form.watch('paises');
  const watchedNombre = form.watch('nombre');
  const watchedUsuarioApp = form.watch('usuarioApp');
  const watchedPassword = form.watch('password');

  // Inline notice shown when the current perfil was auto-cleared because
  // the admin added a country where it doesn't exist (e.g. Nova requires
  // Mexico-only). Cleared as soon as a new perfil is picked.
  const [roleClearedReason, setRoleClearedReason] = useState<string | null>(null);

  // Hide perfiles the current país set doesn't support, but always keep
  // the currently-selected one visible so the admin sees what was there
  // before changing it.
  const availablePerfiles = useMemo(
    () =>
      PERFILES.filter(
        (p) => isPerfilValidForCountries(p, watchedPaises) || p === perfil,
      ),
    [watchedPaises, perfil],
  );

  // When the país set changes and the current perfil no longer fits,
  // clear it and tell the admin why. The submit button stays disabled
  // until they pick a replacement (perfil is a required field).
  useEffect(() => {
    if (!perfil) return;
    if (isPerfilValidForCountries(perfil, watchedPaises)) {
      // Perfil is valid for the current selection — drop the stale notice.
      if (roleClearedReason) setRoleClearedReason(null);
      return;
    }
    setRoleClearedReason(
      `El perfil "${perfil}" no existe en todos los países seleccionados. Seleccioná un nuevo perfil para este usuario.`,
    );
    form.setValue('perfil', '' as unknown as Perfil, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [watchedPaises, perfil, form, roleClearedReason]);

  const togglePais = (label: PaisLabel) => {
    const next = watchedPaises.includes(label)
      ? watchedPaises.filter((p) => p !== label)
      : [...watchedPaises, label];
    form.setValue('paises', next as EditUsuarioValues['paises'], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!watchedNombre?.trim()) missing.push('Nombre');
    if (!watchedUsuarioApp?.trim()) missing.push('Nombre de Usuario');
    if (!perfil) missing.push('Perfil');
    if (watchedPaises.length === 0) missing.push('País');
    if (mode === 'create' && !watchedPassword?.trim()) missing.push('Contraseña');
    return missing;
  }, [watchedNombre, watchedUsuarioApp, perfil, watchedPaises, mode, watchedPassword]);

  const canSubmit = missingFields.length === 0;

  const mutation = useMutation({
    mutationFn: async (values: EditUsuarioValues) => {
      if (mode === 'edit' && usuario) {
        return updateUsuario(usuario.id, editFormToFields(values));
      }
      return createUsuario(createFormToFields(values));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USUARIOS_QUERY_KEY });
      onOpenChange(false);
      onSuccess(
        mode === 'edit'
          ? 'El usuario se actualizó correctamente.'
          : 'El usuario se creó correctamente.',
      );
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'No se pudo guardar el usuario.';
      onOpenChange(false);
      onError(message);
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    const normalized = (s: string) => s.trim().toLowerCase();
    const duplicated = existingUsuarios.find(
      (u) =>
        u.id !== usuario?.id &&
        normalized(u.fields.UsuarioApp_USR ?? '') === normalized(values.usuarioApp),
    );
    if (duplicated) {
      form.setError('usuarioApp', {
        type: 'manual',
        message: 'Ya existe un usuario con ese nombre de acceso.',
      });
      return;
    }
    mutation.mutate(values);
  });

  const fieldError = (name: keyof EditUsuarioValues) =>
    form.formState.errors[name]?.message as string | undefined;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-lg"
        processing={mutation.isPending && (mode === 'edit' ? 'Guardando cambios…' : 'Creando usuario…')}
      >
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? 'Modificá los datos del usuario. Dejá la contraseña vacía para mantener la actual.'
              : 'Completá los datos para dar de alta un nuevo usuario.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <Field label="Nombre" htmlFor="nombre" required error={fieldError('nombre')}>
              <Input
                id="nombre"
                variant="filled"
                placeholder="Ingresar Nombre"
                autoFocus
                {...form.register('nombre')}
              />
            </Field>

            <Field
              label="Nombre de Usuario"
              htmlFor="usuarioApp"
              required
              error={fieldError('usuarioApp')}
            >
              <Input
                id="usuarioApp"
                variant="filled"
                placeholder="Ingresar Usuario"
                autoComplete="off"
                {...form.register('usuarioApp')}
              />
            </Field>

            <Field label="Perfil" required error={fieldError('perfil')}>
              <Select
                value={perfil || undefined}
                onValueChange={(v) => {
                  setRoleClearedReason(null);
                  form.setValue('perfil', v as EditUsuarioValues['perfil'], {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar perfil" />
                </SelectTrigger>
                <SelectContent>
                  {availablePerfiles.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {roleClearedReason && (
                <p className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
                  {roleClearedReason}
                </p>
              )}
            </Field>

            <Field
              label={mode === 'edit' ? 'Nueva contraseña' : 'Contraseña'}
              htmlFor="password"
              required={mode === 'create'}
              error={fieldError('password')}
            >
              <PasswordInput
                id="password"
                variant="filled"
                placeholder={mode === 'edit' ? 'Dejar vacío para mantener' : 'Password'}
                autoComplete="new-password"
                {...form.register('password')}
              />
            </Field>

            <Field
              label="Países"
              required
              error={fieldError('paises')}
              hint="Seleccioná uno o más. Si elegís ambos, el usuario podrá cambiar entre países desde el selector."
            >
              <div className="flex flex-wrap gap-2">
                {PAIS_OPTIONS.map(({ label }) => {
                  const checked = watchedPaises.includes(label);
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => togglePais(label)}
                      aria-pressed={checked}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
                        checked
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-input bg-background text-muted-foreground hover:bg-muted',
                      )}
                    >
                      <span
                        className={cn(
                          'inline-flex h-4 w-4 items-center justify-center rounded-sm border',
                          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                        )}
                        aria-hidden
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      {label}
                    </button>
                  );
                })}
              </div>
            </Field>
          </DialogBody>

          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar: <span className="font-medium text-destructive">{missingFields.join(', ')}</span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {mode === 'edit' ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  required,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required && <span className="text-primary"> *</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
